import { getSupabase, isSupabaseConfigured } from './supabase';
import { DrawingObject, Layer, DEFAULT_LAYERS, DEFAULT_SNAP_SIZE, SnapSize } from '@/types';
import type { Database } from '@/types/supabase';

export { isSupabaseConfigured };

// ── Local types ───────────────────────────────────────────────

export interface SaveProjectInput {
  id?: string;
  name: string;
  objects: DrawingObject[];
  layers: Layer[];
  settings: { snapSize: SnapSize; showGrid: boolean };
}

export interface ProjectSummary {
  id: string;
  name: string;
  updated_at: string;
}

// ── Supabase-backed service ───────────────────────────────────

const TABLE = 'projects' as const;

/**
 * Save (upsert) a project. Returns the project id.
 */
export async function saveProject(project: SaveProjectInput): Promise<string> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  const payload: Database['public']['Tables']['projects']['Insert'] = {
    name: project.name,
    data: {
      objects: project.objects,
      layers: project.layers,
      settings: project.settings,
    },
  };

  if (project.id) {
    payload.id = project.id;
  }

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(payload, { onConflict: 'id' })
    .select('id')
    .single();

  if (error) throw error;
  return data.id;
}

/**
 * Load a single project by id.
 */
export async function loadProject(id: string): Promise<SaveProjectInput & { id: string }> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;

  const d = data.data as { objects?: DrawingObject[]; layers?: Layer[]; settings?: SaveProjectInput['settings'] };
  return {
    id: data.id,
    name: data.name,
    objects: d.objects ?? [],
    layers: d.layers ?? DEFAULT_LAYERS,
    settings: d.settings ?? { snapSize: DEFAULT_SNAP_SIZE, showGrid: true },
  };
}

/**
 * List all projects (summary only — no full data).
 */
export async function listProjects(): Promise<ProjectSummary[]> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  const { data, error } = await supabase
    .from(TABLE)
    .select('id, name, updated_at')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Delete a project by id.
 */
export async function deleteProject(id: string): Promise<void> {
  const supabase = getSupabase();
  if (!supabase) throw new Error('Supabase not configured');

  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw error;
}

// ── localStorage fallback helpers ─────────────────────────────

const LS_KEY = 'bygglov-project';

export function saveToLocalStorage(project: SaveProjectInput): void {
  const data = {
    name: project.name,
    objects: project.objects,
    layers: project.layers,
    settings: project.settings,
    savedAt: new Date().toISOString(),
  };
  localStorage.setItem(LS_KEY, JSON.stringify(data));
}

export function loadFromLocalStorage(): (SaveProjectInput & { id?: string }) | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return {
      id: data.id,
      name: data.name || 'Nytt projekt',
      objects: data.objects || [],
      layers: data.layers || DEFAULT_LAYERS,
      settings: data.settings || { snapSize: DEFAULT_SNAP_SIZE, showGrid: true },
    };
  } catch {
    return null;
  }
}

/**
 * Unified save — Supabase if configured, otherwise localStorage.
 * Returns the project id (from Supabase) or undefined (localStorage).
 */
export async function saveProjectAuto(project: SaveProjectInput): Promise<string | undefined> {
  if (isSupabaseConfigured()) {
    return saveProject(project);
  }
  saveToLocalStorage(project);
  return undefined;
}
